import { foldAccents, LATIN_WORD } from './nameReading'
/**
 * **이름의 소리 결** — 오행 말고, 그 이름만의 소리를 재는 값들.
 *
 * 왜 필요한가. 이름 풀이의 뒤 두 단락이 **사주로만** 정해져서, 한 사람이 어떤 이름을
 * 넣어도 같은 이야기가 나왔다 (제보: 이지연·정성원·이한주가 거의 같은 글). 한국 이름은
 * 첫소리가 흙(ㅇ·ㅎ)·쇠(ㅅ·ㅈ·ㅊ)에 몰려 있어 오행만 보면 셋이 같은 조합이 된다.
 *
 * 그래서 **오행이 같아도 갈리는 값**을 잰다 — 받침, 모음의 밝음, 첫소리의 세기, 음절 수.
 * 이 값들은 AI 에게 재료로 넘어가고, 별명 보기를 고르는 데도 쓴다.
 *
 * 한글은 낱자로 풀어 본다 (가 = 0xAC00, 초성 19 × 중성 21 × 종성 28).
 * 라틴·가나는 글자로 어림잡는다 — 그 쪽은 한 낱말이 한 덩이라 음절을 쪼개지 않는다.
 */

/** 받침의 갈래 */
export type Coda =
  /** 받침이 없다 — 열린 소리 */
  | 'none'
  /** ㄴ ㅁ ㅇ ㄹ — 울리며 이어지는 받침 */
  | 'ringing'
  /** ㄱ ㄷ ㅂ ㅅ … — 닫고 끊는 받침 */
  | 'closing'

/** 모음의 밝음 */
export type Vowel = 'bright' | 'dark' | 'neutral'

/** 첫소리의 세기 */
export type Onset =
  /** ㄲ ㅋ ㅉ ㅊ … 된소리·거센소리 */
  | 'strong'
  /** ㄱ ㄴ ㄷ ㅁ ㅂ ㅅ ㅈ 예사소리 */
  | 'plain'
  /** ㅇ ㅎ — 여리게 시작한다 */
  | 'light'

export interface SoundTraits {
  coda: Coda
  vowel: Vowel
  onset: Onset
}

const HANGUL_START = 0xac00
const HANGUL_END = 0xd7a3

/** 초성 19 — 순서는 유니코드 그대로 */
const ONSET_KIND: Onset[] = [
  'plain', // ㄱ
  'strong', // ㄲ
  'plain', // ㄴ
  'plain', // ㄷ
  'strong', // ㄸ
  'plain', // ㄹ
  'plain', // ㅁ
  'plain', // ㅂ
  'strong', // ㅃ
  'plain', // ㅅ
  'strong', // ㅆ
  'light', // ㅇ
  'plain', // ㅈ
  'strong', // ㅉ
  'strong', // ㅊ
  'strong', // ㅋ
  'strong', // ㅌ
  'strong', // ㅍ
  'light', // ㅎ
]

/** 중성 21 — 양성(밝음) · 음성(어두움) · 중성 */
const VOWEL_KIND: Vowel[] = [
  'bright', // ㅏ
  'bright', // ㅐ
  'bright', // ㅑ
  'bright', // ㅒ
  'dark', // ㅓ
  'dark', // ㅔ
  'dark', // ㅕ
  'dark', // ㅖ
  'bright', // ㅗ
  'bright', // ㅘ
  'bright', // ㅙ
  'bright', // ㅚ
  'bright', // ㅛ
  'dark', // ㅜ
  'dark', // ㅝ
  'dark', // ㅞ
  'dark', // ㅟ
  'dark', // ㅠ
  'dark', // ㅡ
  'neutral', // ㅢ
  'neutral', // ㅣ
]

/** 종성 인덱스 → 낱자 (0 은 받침 없음). ㄴ ㄹ ㅁ ㅇ 은 울리는 받침이다. */
const CODA_LETTERS = ' ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ'
const RINGING_LETTERS = new Set(['ㄴ', 'ㄹ', 'ㅁ', 'ㅇ'])

/** 라틴 글자의 모음 밝음 (첫 모음으로 본다) */
const LATIN_BRIGHT = new Set(['a', 'o'])
const LATIN_DARK = new Set(['e', 'u'])
const LATIN_NEUTRAL = new Set(['i', 'y'])
/** 라틴 첫소리의 세기 — 거센 소리로 들리는 글자들 */
const LATIN_STRONG = new Set(['k', 'q', 'x', 'ch', 'th', 'ph'])
const LATIN_LIGHT = new Set(['a', 'e', 'i', 'o', 'u', 'y', 'h', 'w'])

/**
 * 한 덩이(한글 한 음절, 라틴 한 낱말)의 소리 결.
 * 읽을 수 없으면 `null` — 한자처럼 음을 모르는 글자다.
 */
export function soundTraits(text: string): SoundTraits | null {
  const ch = text.normalize('NFC').trim()
  if (ch === '') return null

  const code = ch.codePointAt(0)!
  if (code >= HANGUL_START && code <= HANGUL_END) {
    const at = code - HANGUL_START
    const onset = ONSET_KIND[Math.floor(at / (21 * 28))]!
    const vowel = VOWEL_KIND[Math.floor(at / 28) % 21]!
    const jong = at % 28
    const letter = CODA_LETTERS[jong]!
    const coda: Coda = jong === 0 ? 'none' : RINGING_LETTERS.has(letter) ? 'ringing' : 'closing'
    return { onset, vowel, coda }
  }

  // 악센트 붙은 낱말(José·João)도 라틴 낱말로 — 소리는 악센트를 걷고 본다
  if (LATIN_WORD.test(ch)) {
    const low = foldAccents(ch).toLowerCase()
    const head = LATIN_STRONG.has(low.slice(0, 2)) ? low.slice(0, 2) : low[0]!
    const onset: Onset = LATIN_STRONG.has(head) ? 'strong' : LATIN_LIGHT.has(head) ? 'light' : 'plain'
    const firstVowel = [...low].find((c) => 'aeiouy'.includes(c))
    const vowel: Vowel =
      firstVowel === undefined ? 'neutral'
      : LATIN_BRIGHT.has(firstVowel) ? 'bright'
      : LATIN_DARK.has(firstVowel) ? 'dark'
      : LATIN_NEUTRAL.has(firstVowel) ? 'neutral'
      : 'neutral'
    const last = low.at(-1)!
    // -ng 는 콧소리로 울리며 끝난다 — 마지막 글자(g)만 보면 zh 王 wáng 을 "또렷하게 끊기는 끝" 으로 썼다
    const coda: Coda = 'aeiouy'.includes(last) ? 'none' : 'nmlr'.includes(last) || low.endsWith('ng') ? 'ringing' : 'closing'
    return { onset, vowel, coda }
  }

  return null
}

/** 이름 전체의 소리 결 — 몇 덩이인가, 받침이 몇 개인가, 밝은 소리가 많은가 */
export interface NameSound {
  /** 읽은 덩이 수 (한글은 음절, 라틴은 낱말) */
  units: number
  /** 받침이 있는 덩이 수 */
  coda: number
  /** 울리는 받침 수 */
  ringing: number
  /** 밝은 모음 수 · 어두운 모음 수 */
  bright: number
  dark: number
  /** 첫 덩이의 결 (없으면 null) */
  first: SoundTraits | null
  /** 끝 덩이의 결 (없으면 null) */
  last: SoundTraits | null
}

export function nameSound(units: readonly string[]): NameSound {
  const traits = units.map(soundTraits).filter((t): t is SoundTraits => t !== null)
  return {
    units: traits.length,
    coda: traits.filter((t) => t.coda !== 'none').length,
    ringing: traits.filter((t) => t.coda === 'ringing').length,
    bright: traits.filter((t) => t.vowel === 'bright').length,
    dark: traits.filter((t) => t.vowel === 'dark').length,
    first: traits[0] ?? null,
    last: traits.at(-1) ?? null,
  }
}

/**
 * **이 이름의 소리를 닮았는가** — 별명 보기를 고를 때 쓰는 점수.
 *
 * 규칙에 맞는 첫소리 목록에서 셋을 고르는데, 그 가운데 **이름의 소리를 살린 것**을
 * 앞에 둔다. 예전에는 이름을 해시로 섞어 자리만 정해서, 목록에 있는 아무 셋이 나왔고
 * "이 이름에서 골라 본" 이라는 말과 맞지 않았다 (제보).
 *
 * 닮은 점 하나에 1점이다 — 끝 모음이 같은가 · 받침 갈래가 같은가 · 덩이 수가 같은가.
 */
export function echoScore(candidate: string, sound: NameSound): number {
  const traits = soundTraits(candidate)
  if (!traits || sound.units === 0) return 0
  /*
   * **이름 전체의 결로 견준다**. 끝 덩이만 보면 이지연과 정성원이 같은 점수를 받아
   * (둘 다 끝이 「연」·「원」 — 여린 첫소리·어두운 모음·울리는 받침) 같은 보기가 나왔다.
   * 받침이 몇 개인지, 어떤 모음이 많은지까지 보면 둘이 갈린다.
   */
  const most = <T,>(a: T, na: number, b: T, nb: number, tie: T): T => (na === nb ? tie : na > nb ? a : b)
  const codaMost: Coda = most<Coda>('ringing', sound.ringing, 'none', sound.units - sound.coda, sound.coda > sound.units - sound.coda ? 'closing' : 'none')
  const vowelMost: Vowel = most<Vowel>('bright', sound.bright, 'dark', sound.dark, 'neutral')
  let score = 0
  if (traits.coda === codaMost) score += 2
  if (traits.vowel === vowelMost) score += 2
  if (sound.last && traits.coda === sound.last.coda) score += 1
  if (sound.last && traits.vowel === sound.last.vowel) score += 1
  if ([...candidate.normalize('NFC')].length === sound.units) score += 1
  return score
}
